/**
 * ONE TAP — procedural animation toolkit.
 *
 * There is no skeletal animation data anywhere in this game. Every pose the player ever
 * sees — the run cycle, the slide, the wall-run, the throw wind-up, the viewmodel recoil —
 * is computed here, from `PlayerState`, every frame. That makes this file the shared
 * vocabulary for `playerRig.ts`, `viewmodel.ts` and `gunModel.ts`.
 *
 * Three rules govern everything in here:
 *
 *  1. **Frame-rate independence is non-negotiable.** Nothing uses `a += (b - a) * 0.1`.
 *     Smoothing goes through `damp()` from `@shared/math`; springs integrate with a fixed
 *     internal substep so a 30fps frame and a 144fps frame settle identically.
 *  2. **Poses are additive.** A `Joint` captures its rest transform once and then
 *     accumulates weighted offsets each frame. That lets "run cycle" + "lean into turn" +
 *     "recoil" + "wounded hunch" all write to the same joint without any of them knowing
 *     about the others, which is what makes blending between movement states cheap.
 *  3. **Determinism where it is visible.** Procedural noise comes from `makeRng()`, seeded
 *     per-rig, so two clients watching the same player see the same idle sway.
 *
 * Client-only: this file imports `three`. It must never be imported from `src/shared`.
 */

import * as THREE from 'three';
import { clamp, clamp01, damp, lerpN, angleDelta, makeRng, TAU, EPSILON } from '@shared/math';

// ---------------------------------------------------------------------------
// Scalar smoothing
// ---------------------------------------------------------------------------

/** `damp()` for angles — takes the short way around the circle. */
export function dampAngle(a: number, b: number, rate: number, dt: number): number {
  return a + angleDelta(a, b) * (1 - Math.exp(-rate * dt));
}

/** Linear approach with a hard per-second rate. Use when you want a constant slew, not an ease. */
export function approach(current: number, target: number, ratePerSec: number, dt: number): number {
  const d = target - current;
  const step = ratePerSec * dt;
  if (d > step) return current + step;
  if (d < -step) return current - step;
  return target;
}

/** Symmetric dead-zone, so tiny numeric noise never drives an animation. */
export function deadzone(v: number, edge: number): number {
  return Math.abs(v) < edge ? 0 : v;
}

// ---------------------------------------------------------------------------
// Springs
//
// A spring is the difference between "the arm moves to where it should be" and "the arm
// *arrives*". Everything with mass in this game — hands, the gun, the name tag, the torso —
// gets one. The integrator substeps so it stays stable at any frame rate.
// ---------------------------------------------------------------------------

/** Largest step the spring integrator will take. Anything longer is split. */
const SPRING_MAX_STEP = 1 / 240;
/** Never substep more than this many times in one frame — a tab stall must not stall the CPU. */
const SPRING_MAX_SUBSTEPS = 16;

export class Spring {
  value: number;
  velocity = 0;
  target: number;
  /** Natural frequency in Hz — how fast it wants to get there. */
  freqHz: number;
  /** 1 = critically damped, <1 overshoots (bouncy), >1 sluggish. */
  damping: number;

  constructor(value = 0, freqHz = 8, damping = 1) {
    this.value = value;
    this.target = value;
    this.freqHz = freqHz;
    this.damping = damping;
  }

  /** Teleport: kills velocity and error. Use on spawn / respawn so nothing swings in from the last life. */
  snap(v: number): this {
    this.value = v;
    this.target = v;
    this.velocity = 0;
    return this;
  }

  /** Add velocity directly — a kick, a recoil, a landing thump. */
  kick(v: number): this {
    this.velocity += v;
    return this;
  }

  update(dt: number): number {
    if (dt <= 0) return this.value;
    const omega = TAU * this.freqHz;
    const steps = Math.min(SPRING_MAX_SUBSTEPS, Math.max(1, Math.ceil(dt / SPRING_MAX_STEP)));
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      const accel = omega * omega * (this.target - this.value) - 2 * this.damping * omega * this.velocity;
      this.velocity += accel * h;
      this.value += this.velocity * h;
    }
    return this.value;
  }
}

/** Three-axis spring. Same integrator, one allocation. */
export class SpringVec3 {
  readonly value = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  readonly target = new THREE.Vector3();
  freqHz: number;
  damping: number;

  constructor(freqHz = 8, damping = 1) {
    this.freqHz = freqHz;
    this.damping = damping;
  }

  snap(x: number, y: number, z: number): this {
    this.value.set(x, y, z);
    this.target.set(x, y, z);
    this.velocity.set(0, 0, 0);
    return this;
  }

  kick(x: number, y: number, z: number): this {
    this.velocity.x += x;
    this.velocity.y += y;
    this.velocity.z += z;
    return this;
  }

  update(dt: number): THREE.Vector3 {
    if (dt <= 0) return this.value;
    const omega = TAU * this.freqHz;
    const k = omega * omega;
    const c = 2 * this.damping * omega;
    const steps = Math.min(SPRING_MAX_SUBSTEPS, Math.max(1, Math.ceil(dt / SPRING_MAX_STEP)));
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      this.velocity.x += (k * (this.target.x - this.value.x) - c * this.velocity.x) * h;
      this.velocity.y += (k * (this.target.y - this.value.y) - c * this.velocity.y) * h;
      this.velocity.z += (k * (this.target.z - this.value.z) - c * this.velocity.z) * h;
      this.value.x += this.velocity.x * h;
      this.value.y += this.velocity.y * h;
      this.value.z += this.velocity.z * h;
    }
    return this.value;
  }
}

/**
 * A spring that always rests at zero — the shape of every recoil, every landing dip, every
 * flinch. `kick()` it and it rings down. Under-damped by default because a dead-flat return
 * reads as a bug; a small overshoot reads as weight.
 */
export class Impulse {
  value = 0;
  velocity = 0;
  freqHz: number;
  damping: number;

  constructor(freqHz = 11, damping = 0.55) {
    this.freqHz = freqHz;
    this.damping = damping;
  }

  kick(amount: number): this {
    this.velocity += amount;
    return this;
  }

  /** Displace instantly rather than adding velocity — a harder, snappier hit. */
  punch(amount: number): this {
    this.value += amount;
    return this;
  }

  reset(): this {
    this.value = 0;
    this.velocity = 0;
    return this;
  }

  update(dt: number): number {
    if (dt <= 0) return this.value;
    const omega = TAU * this.freqHz;
    const steps = Math.min(SPRING_MAX_SUBSTEPS, Math.max(1, Math.ceil(dt / SPRING_MAX_STEP)));
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      const accel = -omega * omega * this.value - 2 * this.damping * omega * this.velocity;
      this.velocity += accel * h;
      this.value += this.velocity * h;
    }
    // Snap the tail to zero so we do not carry a 1e-12 offset forever.
    if (Math.abs(this.value) < 1e-5 && Math.abs(this.velocity) < 1e-4) {
      this.value = 0;
      this.velocity = 0;
    }
    return this.value;
  }
}

// ---------------------------------------------------------------------------
// One-shot timelines
//
// Fire-and-forget animations (throw, catch, dry-fire, pulse) are normalised playheads.
// The pose function reads `t01` and shapes it; the timeline only owns time.
// ---------------------------------------------------------------------------

export class Timeline {
  /** Seconds elapsed since `play()`. */
  time = 0;
  duration: number;
  private running = false;

  constructor(duration = 0.4) {
    this.duration = duration;
  }

  play(duration?: number): this {
    if (duration !== undefined) this.duration = duration;
    this.time = 0;
    this.running = true;
    return this;
  }

  stop(): this {
    this.running = false;
    this.time = this.duration;
    return this;
  }

  get active(): boolean {
    return this.running;
  }

  /** Normalised progress in [0,1]. Zero when idle. */
  get t01(): number {
    if (!this.running) return 0;
    return this.duration <= EPSILON ? 1 : clamp01(this.time / this.duration);
  }

  /** Advance. Returns the progress for this frame; the timeline stops on the frame it reaches 1. */
  update(dt: number): number {
    if (!this.running) return 0;
    this.time += dt;
    if (this.time >= this.duration) {
      this.running = false;
      return 1;
    }
    return this.duration <= EPSILON ? 1 : this.time / this.duration;
  }
}

// ---------------------------------------------------------------------------
// Easing
// ---------------------------------------------------------------------------

export const easeInQuad = (t: number): number => t * t;
export const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t);
export const easeInCubic = (t: number): number => t * t * t;
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
export const easeOutQuint = (t: number): number => 1 - Math.pow(1 - t, 5);
export const easeOutExpo = (t: number): number => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
export const easeInExpo = (t: number): number => (t <= 0 ? 0 : Math.pow(2, 10 * t - 10));

/** Overshoots past 1 then settles. The "snap" in a catch or a slide-rack. */
export function easeOutBack(t: number, overshoot = 1.9): number {
  const c3 = overshoot + 1;
  const x = t - 1;
  return 1 + c3 * x * x * x + overshoot * x * x;
}

/** Rings past the target a few times. Springy, cartoonish — use sparingly. */
export function easeOutElastic(t: number, periods = 3): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const p = 1 / periods;
  return Math.pow(2, -10 * t) * Math.sin(((t - p / 4) * TAU) / p) + 1;
}

/** 0 → 1 → 0. The shape of any "and back again" beat. */
export const bell = (t: number): number => {
  const x = clamp01(t);
  return Math.sin(x * Math.PI);
};

/** Fast attack, slow release — the shape of an impact. */
export function attackRelease(t: number, attack = 0.15): number {
  const x = clamp01(t);
  if (x < attack) return easeOutQuad(x / attack);
  return 1 - easeInOutCubic((x - attack) / (1 - attack));
}

/** A decaying oscillation sampled at `t01`. Recoil ring-down without a spring. */
export function punchCurve(t: number, freq = 3, decay = 6): number {
  const x = clamp01(t);
  return Math.sin(x * TAU * freq) * Math.exp(-x * decay);
}

/** Smooth pulse that rises over `edge0` and falls after `edge1`, for windowed weights. */
export function window01(t: number, edge0: number, edge1: number, fade = 0.15): number {
  const rise = clamp01((t - edge0) / Math.max(EPSILON, fade));
  const fall = 1 - clamp01((t - edge1) / Math.max(EPSILON, fade));
  const w = Math.min(rise, fall);
  return w * w * (3 - 2 * w);
}

// ---------------------------------------------------------------------------
// Deterministic noise — idle sway, hand tremor, wounded shake
// ---------------------------------------------------------------------------

/** Smooth value noise over a power-of-two table. Seeded, so every client agrees. */
export function makeNoise1(seed: number, size = 256): (t: number) => number {
  const rng = makeRng(seed >>> 0);
  const table = new Float32Array(size);
  for (let i = 0; i < size; i++) table[i] = rng() * 2 - 1;
  const mask = size - 1;
  return function noise1(t: number): number {
    const i = Math.floor(t);
    const f = t - i;
    const a = table[i & mask]!;
    const b = table[(i + 1) & mask]!;
    const s = f * f * (3 - 2 * f);
    return a + (b - a) * s;
  };
}

/** Three octaves of `makeNoise1`. Organic drift for weapon sway and breathing. */
export function makeFbm1(seed: number): (t: number) => number {
  const n = makeNoise1(seed);
  return function fbm(t: number): number {
    return n(t) * 0.55 + n(t * 2.13 + 31.7) * 0.3 + n(t * 4.37 + 77.1) * 0.15;
  };
}

// ---------------------------------------------------------------------------
// Additive pose accumulation
// ---------------------------------------------------------------------------

const _q = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, 'YXZ');

/**
 * A wrapper around one `Object3D` that remembers its rest transform and lets any number of
 * pose functions layer weighted offsets onto it in a single frame.
 *
 * Usage per frame:
 *   joint.begin();
 *   poseRun(joint, ...);      // joint.rot(...) / joint.move(...)
 *   poseLean(joint, ...);     // adds on top
 *   joint.end();              // writes the composed transform
 *
 * Rotations sum as YXZ Euler offsets applied *after* the rest orientation, which for limb
 * segments (short arcs around a known axis) is exactly what you want and costs one quaternion.
 */
export class Joint {
  readonly object: THREE.Object3D;

  private readonly restPos = new THREE.Vector3();
  private readonly restQuat = new THREE.Quaternion();
  private readonly restScale = new THREE.Vector3(1, 1, 1);

  private px = 0;
  private py = 0;
  private pz = 0;
  private rx = 0;
  private ry = 0;
  private rz = 0;
  private sx = 1;
  private sy = 1;
  private sz = 1;

  constructor(object: THREE.Object3D) {
    this.object = object;
    this.capture();
  }

  /** Re-read the object's current transform as the new rest pose. */
  capture(): this {
    this.restPos.copy(this.object.position);
    this.restQuat.copy(this.object.quaternion);
    this.restScale.copy(this.object.scale);
    return this;
  }

  /** Clear this frame's accumulators. Call once, before any pose contributes. */
  begin(): this {
    this.px = this.py = this.pz = 0;
    this.rx = this.ry = this.rz = 0;
    this.sx = this.sy = this.sz = 1;
    return this;
  }

  /** Additive local rotation in radians, scaled by `w`. */
  rot(x: number, y: number, z: number, w = 1): this {
    this.rx += x * w;
    this.ry += y * w;
    this.rz += z * w;
    return this;
  }

  /** Additive local translation, scaled by `w`. */
  move(x: number, y: number, z: number, w = 1): this {
    this.px += x * w;
    this.py += y * w;
    this.pz += z * w;
    return this;
  }

  /** Multiplicative local scale (1,1,1 is neutral), blended toward by `w`. */
  scl(x: number, y: number, z: number, w = 1): this {
    this.sx *= lerpN(1, x, w);
    this.sy *= lerpN(1, y, w);
    this.sz *= lerpN(1, z, w);
    return this;
  }

  /** Uniform squash/stretch about the joint origin. */
  squash(amount: number, w = 1): this {
    return this.scl(1 - amount * 0.5, 1 + amount, 1 - amount * 0.5, w);
  }

  /** Compose and write to the object. */
  end(): void {
    const o = this.object;
    o.position.set(this.restPos.x + this.px, this.restPos.y + this.py, this.restPos.z + this.pz);
    if (this.rx !== 0 || this.ry !== 0 || this.rz !== 0) {
      _e.set(this.rx, this.ry, this.rz, 'YXZ');
      _q.setFromEuler(_e);
      o.quaternion.copy(this.restQuat).multiply(_q);
    } else {
      o.quaternion.copy(this.restQuat);
    }
    o.scale.set(this.restScale.x * this.sx, this.restScale.y * this.sy, this.restScale.z * this.sz);
  }

  /** Rest-pose accessors, for pose code that needs to reason in absolute local space. */
  get rest(): THREE.Vector3 {
    return this.restPos;
  }
}

// ---------------------------------------------------------------------------
// Two-bone IK
//
// The leg cycle is driven by foot *targets*, not by hand-authored knee angles — that is what
// keeps feet on the floor when the hip height changes (crouch, slide, landing compression)
// instead of skating or sinking.
// ---------------------------------------------------------------------------

const _ikAxis = new THREE.Vector3();
const _ikPole = new THREE.Vector3();
const _ikTmp = new THREE.Vector3();

/**
 * Analytic two-bone solve. Given a root, a target, two segment lengths and a preferred bend
 * direction, writes the middle joint (knee/elbow) position into `outJoint` and returns the
 * clamped effective target distance.
 *
 * All vectors are in the same space; the caller decides which.
 */
export function solveTwoBoneIK(
  root: THREE.Vector3,
  target: THREE.Vector3,
  upperLen: number,
  lowerLen: number,
  poleDir: THREE.Vector3,
  outJoint: THREE.Vector3,
): number {
  _ikAxis.subVectors(target, root);
  let d = _ikAxis.length();
  const minD = Math.abs(upperLen - lowerLen) + 1e-3;
  const maxD = upperLen + lowerLen - 1e-3;
  if (d < 1e-5) {
    // Fully collapsed: fabricate an axis so the solve stays defined.
    _ikAxis.set(0, -1, 0);
    d = minD;
  } else {
    _ikAxis.multiplyScalar(1 / d);
    d = clamp(d, minD, maxD);
  }

  const a = (upperLen * upperLen - lowerLen * lowerLen + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, upperLen * upperLen - a * a));

  // Orthogonalise the pole against the root->target axis so the bend plane is well defined.
  _ikPole.copy(poleDir);
  _ikPole.addScaledVector(_ikAxis, -_ikPole.dot(_ikAxis));
  if (_ikPole.lengthSq() < 1e-8) {
    // Pole is parallel to the limb. Pick any perpendicular; the limb is straight anyway.
    _ikTmp.set(0, 0, 1);
    if (Math.abs(_ikAxis.z) > 0.9) _ikTmp.set(1, 0, 0);
    _ikPole.crossVectors(_ikAxis, _ikTmp);
  }
  _ikPole.normalize();

  outJoint.copy(root).addScaledVector(_ikAxis, a).addScaledVector(_ikPole, h);
  return d;
}

const _aimDir = new THREE.Vector3();
const BONE_AXIS = new THREE.Vector3(0, -1, 0);

/**
 * Point a bone at a target. Bone meshes in this project are authored along -Y with their
 * origin at the proximal end, so a bone "aims" by rotating -Y onto the segment direction.
 * Returns the segment length so callers can stretch the mesh to close any IK gap.
 */
export function aimBone(bone: THREE.Object3D, from: THREE.Vector3, to: THREE.Vector3): number {
  _aimDir.subVectors(to, from);
  const len = _aimDir.length();
  bone.position.copy(from);
  if (len < 1e-6) {
    bone.quaternion.identity();
    return 0;
  }
  _aimDir.multiplyScalar(1 / len);
  bone.quaternion.setFromUnitVectors(BONE_AXIS, _aimDir);
  return len;
}

// ---------------------------------------------------------------------------
// Gait
// ---------------------------------------------------------------------------

/**
 * Foot path for one leg of a run cycle, in the hip's local frame.
 *
 * `phase` is in turns (0..1). The contact half of the cycle drags the foot backwards along
 * the ground; the swing half lifts it and throws it forward. `stride` scales reach, `lift`
 * scales how high the foot picks up. Writes into `out` (x = lateral offset is the caller's
 * job, y = height, z = forward, -Z being forward).
 */
export function footPath(phase: number, stride: number, lift: number, out: THREE.Vector3): THREE.Vector3 {
  const p = phase - Math.floor(phase);
  if (p < 0.5) {
    // Contact / drive: linear sweep from front to back, foot planted.
    const k = p / 0.5;
    out.z = lerpN(-stride, stride, k);
    out.y = 0;
  } else {
    // Swing: arc from back to front with a lifted knee-through.
    const k = (p - 0.5) / 0.5;
    const e = easeInOutCubic(k);
    out.z = lerpN(stride, -stride, e);
    out.y = Math.sin(k * Math.PI) * lift;
  }
  out.x = 0;
  return out;
}

/** Vertical bob of the pelvis for a given gait phase — two dips per stride. */
export function gaitBob(phase: number, amount: number): number {
  return -Math.abs(Math.sin(phase * Math.PI * 2)) * amount;
}

// ---------------------------------------------------------------------------
// Material helpers
//
// `makeToonMaterial()` returns an opaque `THREE.Material`, so these narrow it structurally
// rather than assuming a concrete class. That keeps this file working no matter which
// material `toon.ts` decides to build on.
// ---------------------------------------------------------------------------

interface TintableMaterial extends THREE.Material {
  color?: THREE.Color;
  emissive?: THREE.Color;
  emissiveIntensity?: number;
}

export function setColor(m: THREE.Material | THREE.Material[], hex: number): void {
  if (Array.isArray(m)) {
    for (const sub of m) setColor(sub, hex);
    return;
  }
  const t = m as TintableMaterial;
  if (t.color) t.color.setHex(hex);
}

export function setEmissive(m: THREE.Material | THREE.Material[], hex: number, intensity = 1): void {
  if (Array.isArray(m)) {
    for (const sub of m) setEmissive(sub, hex, intensity);
    return;
  }
  const t = m as TintableMaterial;
  if (t.emissive) t.emissive.setHex(hex);
  if ('emissiveIntensity' in t) t.emissiveIntensity = intensity;
}

export function setOpacity(m: THREE.Material | THREE.Material[], v: number): void {
  if (Array.isArray(m)) {
    for (const sub of m) setOpacity(sub, v);
    return;
  }
  m.opacity = v;
  m.transparent = v < 1;
}

const _mixA = new THREE.Color();
const _mixB = new THREE.Color();

/** Blend two hex colours in linear-ish sRGB space. Returns a new hex. */
export function mixHex(a: number, b: number, t: number): number {
  _mixA.setHex(a);
  _mixB.setHex(b);
  _mixA.lerp(_mixB, clamp01(t));
  return _mixA.getHex();
}

/** Scale a hex colour's brightness. */
export function scaleHex(a: number, k: number): number {
  _mixA.setHex(a);
  _mixA.multiplyScalar(k);
  _mixA.r = clamp01(_mixA.r);
  _mixA.g = clamp01(_mixA.g);
  _mixA.b = clamp01(_mixA.b);
  return _mixA.getHex();
}

// ---------------------------------------------------------------------------
// Teardown
// ---------------------------------------------------------------------------

/**
 * Release every geometry, material and texture under `root`, then detach it.
 * Rigs are created and destroyed every round; without this a long match leaks the GPU dry.
 */
export function disposeObject3D(root: THREE.Object3D): void {
  const seenGeo = new Set<THREE.BufferGeometry>();
  const seenMat = new Set<THREE.Material>();

  root.traverse((o) => {
    const anyO = o as THREE.Mesh & THREE.Points & THREE.Sprite & THREE.Line;
    const geo = anyO.geometry as THREE.BufferGeometry | undefined;
    if (geo && !seenGeo.has(geo)) {
      seenGeo.add(geo);
      geo.dispose();
    }
    const mat = anyO.material as THREE.Material | THREE.Material[] | undefined;
    if (!mat) return;
    const list = Array.isArray(mat) ? mat : [mat];
    for (const m of list) {
      if (seenMat.has(m)) continue;
      seenMat.add(m);
      disposeMaterialTextures(m);
      m.dispose();
    }
  });

  root.removeFromParent();
}

const TEXTURE_SLOTS = [
  'map',
  'alphaMap',
  'emissiveMap',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'gradientMap',
  'bumpMap',
  'lightMap',
] as const;

/**
 * Texture ownership is **opt-in**, and that is deliberate.
 *
 * Materials in this project routinely carry textures they did not create: `makeToonMaterial`
 * hands every toon material the same module-level 3-band gradient ramp, and the glow, icon
 * and muzzle-flash atlases here are cached per-module. If `disposeObject3D` freed textures by
 * default, the first player rig destroyed at the end of a round would take the shared ramp
 * down with it and every surviving material in the game would render black.
 *
 * So: only textures explicitly marked with `markOwned()` are disposed automatically.
 * Everything else is somebody else's problem, which is exactly right.
 */
export function markOwned<T extends THREE.Texture>(tex: T): T {
  tex.userData['owned'] = true;
  return tex;
}

/** Documents that a texture lives in a module-level cache and must outlive its users. */
export function markShared<T extends THREE.Texture>(tex: T): T {
  tex.userData['shared'] = true;
  return tex;
}

function disposeTexture(tex: unknown): void {
  if (tex instanceof THREE.Texture && tex.userData['owned'] === true) tex.dispose();
}

function disposeMaterialTextures(m: THREE.Material): void {
  const rec = m as unknown as Record<string, unknown>;
  for (const slot of TEXTURE_SLOTS) disposeTexture(rec[slot]);
  const uniforms = rec['uniforms'] as Record<string, { value?: unknown }> | undefined;
  if (uniforms) {
    for (const key of Object.keys(uniforms)) disposeTexture(uniforms[key]?.value);
  }
}

// ---------------------------------------------------------------------------
// Small geometry helpers shared by the rig, the viewmodel and the gun
// ---------------------------------------------------------------------------

/**
 * A chunky bevelled slab. This is the single most-used shape in ONE TAP's art direction:
 * every hard-edged box gets a bevel so the inverted-hull outline catches a highlight along
 * the chamfer instead of reading as a flat untextured cube.
 *
 * Built from an extruded rounded rectangle, centred on the origin, depth along Z.
 */
export function bevelBox(
  width: number,
  height: number,
  depth: number,
  bevel = 0.02,
  round = 0.03,
  curveSegments = 3,
): THREE.BufferGeometry {
  const b = Math.min(bevel, width * 0.45, height * 0.45, depth * 0.45);
  const r = Math.min(round, width * 0.49 - b, height * 0.49 - b);
  const hw = width * 0.5 - b;
  const hh = height * 0.5 - b;

  const shape = new THREE.Shape();
  const rr = Math.max(0, r);
  shape.moveTo(-hw + rr, -hh);
  shape.lineTo(hw - rr, -hh);
  if (rr > 0) shape.quadraticCurveTo(hw, -hh, hw, -hh + rr);
  shape.lineTo(hw, hh - rr);
  if (rr > 0) shape.quadraticCurveTo(hw, hh, hw - rr, hh);
  shape.lineTo(-hw + rr, hh);
  if (rr > 0) shape.quadraticCurveTo(-hw, hh, -hw, hh - rr);
  shape.lineTo(-hw, -hh + rr);
  if (rr > 0) shape.quadraticCurveTo(-hw, -hh, -hw + rr, -hh);

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(1e-3, depth - b * 2),
    bevelEnabled: b > 1e-4,
    bevelThickness: b,
    bevelSize: b,
    bevelOffset: 0,
    bevelSegments: 1,
    curveSegments,
    steps: 1,
  });
  geo.translate(0, 0, -(depth - b * 2) * 0.5);
  geo.computeVertexNormals();
  return geo;
}

/**
 * A tapered bevelled slab — wider at one end than the other. Limbs and gun housings taper;
 * parallel-sided boxes look like programmer art.
 */
export function taperBox(
  width: number,
  height: number,
  depth: number,
  taper: number,
  bevel = 0.015,
): THREE.BufferGeometry {
  const geo = bevelBox(width, height, depth, bevel, bevel * 1.5);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const halfH = height * 0.5;
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const k = clamp01((y + halfH) / Math.max(EPSILON, height));
    const s = lerpN(1, taper, k);
    pos.setX(i, pos.getX(i) * s);
    pos.setZ(i, pos.getZ(i) * s);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * A lathed solid of revolution around +Y from a `[radius, y]` profile.
 * Pass `forward = true` to reorient it down -Z, which is the barrel convention used by
 * `gunModel.ts` (and matches `dirFromYawPitch`, where yaw 0 looks down -Z).
 */
export function lathe(profile: readonly (readonly [number, number])[], segments = 16, forward = false): THREE.BufferGeometry {
  const pts: THREE.Vector2[] = [];
  for (const [r, y] of profile) pts.push(new THREE.Vector2(Math.max(1e-4, r), y));
  const geo = new THREE.LatheGeometry(pts, segments);
  if (forward) geo.rotateX(-Math.PI / 2);
  geo.computeVertexNormals();
  return geo;
}

// ---------------------------------------------------------------------------
// Geometry batching
//
// A stylised character built from bevelled parts is dozens of little meshes, and a 3v3
// match draws six of them plus two guns plus a viewmodel — before the inverted-hull
// outline pass doubles everything. DESIGN.md §7 asks for a locked 60fps on integrated
// graphics, and that budget is spent on draw calls long before it is spent on triangles.
//
// So anything that never moves relative to its bone gets baked into one mesh per material.
// Only the parts that actually animate (a racking slide, a spinning core, a scaling ring)
// stay as separate objects.
// ---------------------------------------------------------------------------

/**
 * Concatenate non-indexed geometries with matching attribute sets into one buffer.
 *
 * Deliberately hand-rolled rather than pulled from `examples/jsm` — every geometry in this
 * project comes from the helpers above, so the attribute layout is known to be exactly
 * position/normal/uv and the general-purpose merger's compatibility checks are dead weight.
 */
export function mergeGeometryList(list: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const owned: THREE.BufferGeometry[] = [];
  for (const g of list) {
    if (g.index) {
      const flat = g.toNonIndexed();
      owned.push(flat);
      parts.push(flat);
    } else {
      parts.push(g);
    }
  }

  let total = 0;
  for (const g of parts) total += g.getAttribute('position').count;

  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  const uv = new Float32Array(total * 2);
  let o3 = 0;
  let o2 = 0;
  for (const g of parts) {
    const P = g.getAttribute('position');
    const N = g.getAttribute('normal') as THREE.BufferAttribute | undefined;
    const U = g.getAttribute('uv') as THREE.BufferAttribute | undefined;
    for (let i = 0; i < P.count; i++) {
      pos[o3 + i * 3] = P.getX(i);
      pos[o3 + i * 3 + 1] = P.getY(i);
      pos[o3 + i * 3 + 2] = P.getZ(i);
      if (N) {
        nrm[o3 + i * 3] = N.getX(i);
        nrm[o3 + i * 3 + 1] = N.getY(i);
        nrm[o3 + i * 3 + 2] = N.getZ(i);
      }
      if (U) {
        uv[o2 + i * 2] = U.getX(i);
        uv[o2 + i * 2 + 1] = U.getY(i);
      }
    }
    o3 += P.count * 3;
    o2 += P.count * 2;
  }

  for (const g of owned) g.dispose();

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  out.computeBoundingSphere();
  return out;
}

/**
 * Collects transformed geometry into one bucket per material, then emits a single merged
 * mesh for each bucket under a given parent.
 *
 * Source geometries are consumed: the batcher bakes the transform into a copy and disposes
 * the original, so callers can build throwaway primitives inline without leaking them.
 */
export class MeshBatcher {
  private readonly buckets = new Map<THREE.Material, THREE.BufferGeometry[]>();

  /** Add a part. `matrix` bakes the part's placement into the merged buffer. */
  add(material: THREE.Material, geometry: THREE.BufferGeometry, matrix?: THREE.Matrix4): this {
    const baked = geometry.clone();
    if (matrix) baked.applyMatrix4(matrix);
    geometry.dispose();
    const list = this.buckets.get(material);
    if (list) list.push(baked);
    else this.buckets.set(material, [baked]);
    return this;
  }

  get size(): number {
    return this.buckets.size;
  }

  /**
   * Merge and attach. Returns the created meshes in insertion order — the first is always
   * the bucket that was written to first, which by convention is the part you want to hang
   * an outline on.
   */
  flush(parent: THREE.Object3D, opts?: { castShadow?: boolean; receiveShadow?: boolean; name?: string }): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    let i = 0;
    for (const [material, list] of this.buckets) {
      const geo = mergeGeometryList(list);
      for (const g of list) g.dispose();
      const m = new THREE.Mesh(geo, material);
      m.name = `${opts?.name ?? (parent.name || 'batch')}:${i++}`;
      m.castShadow = opts?.castShadow ?? true;
      m.receiveShadow = opts?.receiveShadow ?? true;
      parent.add(m);
      out.push(m);
    }
    this.buckets.clear();
    return out;
  }
}

/** Compose a placement matrix without allocating. Rotation order is YXZ, like every Joint. */
const _tmPos = new THREE.Vector3();
const _tmQuat = new THREE.Quaternion();
const _tmScale = new THREE.Vector3(1, 1, 1);
const _tmEuler = new THREE.Euler(0, 0, 0, 'YXZ');
const _tmMatrix = new THREE.Matrix4();

export function place(
  x = 0,
  y = 0,
  z = 0,
  rx = 0,
  ry = 0,
  rz = 0,
  sx = 1,
  sy = 1,
  sz = 1,
): THREE.Matrix4 {
  _tmPos.set(x, y, z);
  _tmEuler.set(rx, ry, rz, 'YXZ');
  _tmQuat.setFromEuler(_tmEuler);
  _tmScale.set(sx, sy, sz);
  return _tmMatrix.compose(_tmPos, _tmQuat, _tmScale);
}

/** A soft radial dot, generated once and shared. Used for glows, drips and muzzle bloom. */
export function radialTexture(inner = 0.0, softness = 1.0, size = 128): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, (size / 2) * inner, size / 2, size / 2, size / 2);
    const stops = 8;
    for (let i = 0; i <= stops; i++) {
      const t = i / stops;
      const a = Math.pow(1 - t, 1 + softness * 2);
      g.addColorStop(t, `rgba(255,255,255,${a.toFixed(4)})`);
    }
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** A hard-edged star burst, for muzzle flashes. Ink-style: no soft gradients, just spikes. */
export function starTexture(points = 6, size = 128): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const cx = size / 2;
    const cy = size / 2;
    const outer = size * 0.5;
    const inner = size * 0.13;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
      const a = (i / (points * 2)) * TAU - Math.PI / 2;
      const r = i % 2 === 0 ? outer : inner;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    // A hot core so the middle blows out to white through bloom.
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, inner * 2.2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, inner * 2.2, 0, TAU);
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
